import { FC, memo } from 'react';

import { RegionProps } from '@components/views/hotel/listing/components/FilterHotelRegion/ListingFilterHotelRegionInterface';
import { useFiltersContext } from '@contexts/Filters';
import SingleSelectionFilter from '@components/ui/filters/SingleSelectionFilter/SingleSelectionFilter';
import PriceRangeFilter from '@components/ui/filters/PriceRangeFilter/PriceRangeFilter';
import NameFilter from '@components/ui/filters/NameFilter/NameFilter';
import ClearAllFilters from '@components/views/components/ClearAllFilters/ClearAllFilters';
import {
  StyledFilters,
  StyledResponsiveFilterCollapse,
  StyledResponsiveHotelNameFilter,
  StyledResponsiveSingleSelectionFilterContainer,
  StyledResponsiveSingleSelectionFilterItemContainer,
} from '@components/views/components/listing/drawer/ResponsiveListingFilterAndSortStyle';
import {
  ListingFilterItemInterface,
  ListingFilterItemKey,
} from '@components/ui/filters/MultiSelectionFilter/types';
import { MinMaxError } from '@components/ui/filters/PriceRangeFilter/utils';
import { FlexibleDiv, Separator } from '@components/formStyles';

import TourFilterSection from '../../listing/TourFilterSection';

interface MobileTourInterface {
  dataRegions: Array<RegionProps> | any;
  onRegionCheck: (checkedKeysValue: ListingFilterItemKey[]) => void;
  checkedKeys: React.Key[];
  tourName: string;
  setTourName: (val: string) => void;
  isAvailableProperty: boolean;
  isAvailablePropertyCount: number;
  handleAvailableProperty: (val: boolean) => void;

  freeCancellation: boolean;
  freeCancellationCount: number;
  handleFreeCancellation: (val: boolean) => void;

  selectedThemeItems: Array<ListingFilterItemKey>;
  dataThemes: Array<ListingFilterItemInterface>;
  handleThemesChecked: (itemKeys: ListingFilterItemKey[]) => void;

  selectedAccTypes: Array<ListingFilterItemKey>;
  dataAccTypes: Array<ListingFilterItemInterface>;
  handleAccTypesChecked: (itemKeys: ListingFilterItemKey[]) => void;

  selectedFeatureItems: Array<ListingFilterItemKey>;
  dataFeatures: Array<ListingFilterItemInterface>;
  handleFeaturesChecked: (itemKeys: ListingFilterItemKey[]) => void;

  selectedDatesItems: Array<ListingFilterItemKey>;
  dataDates: Array<ListingFilterItemInterface>;
  handleDatesChecked: (itemKeys: ListingFilterItemKey[]) => void;
  selectedDepartureItems: Array<ListingFilterItemKey>;
  dataDepartures: Array<ListingFilterItemInterface>;
  handleDeparturesChecked: (itemKeys: ListingFilterItemKey[]) => void;
  selectedTransportationItems: Array<ListingFilterItemKey>;
  dataTransportations: Array<ListingFilterItemInterface>;
  handleTransportationsChecked: (itemKeys: ListingFilterItemKey[]) => void;
  selectedVisaItems: Array<ListingFilterItemKey>;
  dataVisas: Array<ListingFilterItemInterface>;
  handleVisasChecked: (itemKeys: ListingFilterItemKey[]) => void;
  selectedSpecialPeriodItems: Array<ListingFilterItemKey>;
  dataSpecialPeriods: Array<ListingFilterItemInterface>;
  handleSpecialPeriodsChecked: (itemKeys: ListingFilterItemKey[]) => void;

  minValue: number | undefined;
  maxValue: number | undefined;
  setMinValue: (val: number | undefined) => void;
  setMaxValue: (val: number | undefined) => void;
  minMaxError?: MinMaxError;
  clearAllFilters: () => void;
  disableClearAllFilters?: boolean;
  handleRemoveSpecificFilter: (filterKey: string) => void;
  checkedRegionKeys: ListingFilterItemKey[];
}

const MobileTourFilters: FC<MobileTourInterface> = ({
  dataRegions,
  onRegionCheck,
  tourName,
  setTourName,
  handleAvailableProperty,
  isAvailableProperty,
  isAvailablePropertyCount,
  freeCancellation,
  freeCancellationCount,
  handleFreeCancellation,
  selectedThemeItems,
  dataThemes,
  handleThemesChecked,
  selectedAccTypes,
  dataAccTypes,
  handleAccTypesChecked,
  dataFeatures,
  selectedFeatureItems,
  handleFeaturesChecked,
  dataDates,
  selectedDatesItems,
  handleDatesChecked,
  dataDepartures,
  selectedDepartureItems,
  handleDeparturesChecked,
  dataTransportations,
  selectedTransportationItems,
  handleTransportationsChecked,
  dataVisas,
  selectedVisaItems,
  handleVisasChecked,
  dataSpecialPeriods,
  selectedSpecialPeriodItems,
  handleSpecialPeriodsChecked,
  minValue,
  maxValue,
  minMaxError,
  setMinValue,
  setMaxValue,
  clearAllFilters,
  disableClearAllFilters,
  checkedRegionKeys,
}) => {
  const { setFilters } = useFiltersContext();

  return (
    <StyledFilters>
      <FlexibleDiv top={24}>
        <ClearAllFilters
          onClick={clearAllFilters}
          disabled={disableClearAllFilters}
        />
      </FlexibleDiv>
      <StyledResponsiveFilterCollapse>
        {dataRegions?.length > 0 ? (
          <TourFilterSection
            title='Bölge'
            data={dataRegions}
            handleSelectedItems={onRegionCheck}
            selectedItems={checkedRegionKeys}
            searchable
            collapsible
          />
        ) : null}

        {(isAvailablePropertyCount && isAvailablePropertyCount > 0) ||
        (freeCancellationCount && freeCancellationCount > 0) ? (
          <StyledResponsiveSingleSelectionFilterContainer>
            {isAvailablePropertyCount && isAvailablePropertyCount > 0 ? (
              <StyledResponsiveSingleSelectionFilterItemContainer>
                <SingleSelectionFilter
                  className='responsive-single-selection-filter'
                  title='Sadece yer olan turları göster'
                  subTitle={isAvailablePropertyCount}
                  checked={isAvailableProperty}
                  handleCheckBoxChange={handleAvailableProperty}
                  mode='controlled'
                />
              </StyledResponsiveSingleSelectionFilterItemContainer>
            ) : null}
            {freeCancellationCount && freeCancellationCount > 0 ? (
              <SingleSelectionFilter
                title='İptal Politikası'
                subTitle={freeCancellationCount}
                checked={freeCancellation}
                handleCheckBoxChange={handleFreeCancellation}
                mode='controlled'
              />
            ) : null}
          </StyledResponsiveSingleSelectionFilterContainer>
        ) : null}

        <StyledResponsiveHotelNameFilter>
          <NameFilter
            title='Tur Adı'
            name={tourName}
            setName={setTourName}
            placeholder='Tur adı ile arayın'
            handleSearch={(name) =>
              setFilters((prevFilters) => ({
                ...prevFilters,
                hotelName: name,
              }))
            }
          />
        </StyledResponsiveHotelNameFilter>

        <PriceRangeFilter
          minValue={minValue}
          maxValue={maxValue}
          minMaxError={minMaxError}
          setMinValue={setMinValue}
          setMaxValue={setMaxValue}
          title='Konaklama Fiyatı'
        />

        <TourFilterSection
          title='Ulaşım Tipi'
          data={dataTransportations || []}
          selectedItems={selectedTransportationItems}
          handleSelectedItems={handleTransportationsChecked}
        />
        <TourFilterSection
          title='Vize Tipi'
          data={dataVisas || []}
          selectedItems={selectedVisaItems}
          handleSelectedItems={handleVisasChecked}
          collapsible
        />

        <TourFilterSection
          title='Özel Dönem'
          data={dataSpecialPeriods || []}
          selectedItems={selectedSpecialPeriodItems}
          handleSelectedItems={handleSpecialPeriodsChecked}
          collapsible
        />
        <TourFilterSection
          title='Tur Temaları'
          data={dataThemes || []}
          selectedItems={selectedThemeItems}
          handleSelectedItems={handleThemesChecked}
          searchable
          collapsible
        />
        <TourFilterSection
          title='Tur Özellikleri'
          data={dataFeatures || []}
          selectedItems={selectedFeatureItems}
          handleSelectedItems={handleFeaturesChecked}
          searchable
          collapsible
        />
        <TourFilterSection
          title='Tur Kalkış Tarihleri'
          data={dataDates || []}
          selectedItems={selectedDatesItems}
          handleSelectedItems={handleDatesChecked}
          collapsible
        />
        <TourFilterSection
          title='Çıkış Noktası'
          data={dataDepartures || []}
          selectedItems={selectedDepartureItems}
          handleSelectedItems={handleDeparturesChecked}
          collapsible
        />
        {dataAccTypes?.length > 0 ? (
          <>
            <TourFilterSection
              title='Konaklama Tipi'
              selectedItems={selectedAccTypes}
              data={dataAccTypes}
              handleSelectedItems={handleAccTypesChecked}
              collapsible
            />
            <Separator />
          </>
        ) : null}
      </StyledResponsiveFilterCollapse>
    </StyledFilters>
  );
};

export default memo(MobileTourFilters);
